// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/team-member.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { teamMembersApi } from "../../api/team-members";
import type { TeamMember } from "../../api/types";
// override-block:imports BEGIN
import { createElement } from "react";
import { useQuery } from "@tanstack/react-query";
import type { TeamMemberStatus } from "../../api/types";
// CHG-2958 (Slice J / UM-R12) — Sessions tab fetcher (live endpoint
// GET /platform/members/accounts/{id}/sessions, CHG-2957 / Slice H).
import { getTeamMemberSessions, type TeamMemberSessionEntry } from "../../api/team-members";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<TeamMemberStatus, string> = {
  active: "Active",
  pending: "Pending",
  disabled: "Disabled",
  locked: "Locked",
};

// Status → tone token. Locked + disabled both lean people (the "access /
// identity" gradient) because they're access-control states, not security
// incidents — a locked account is temporary and typically self-resolves
// (timeout, unlock command). Pending leans home (onboarding / neutral).
// Active leans giving (positive admin signal). If a future iteration adds
// a security-incident status it should use g-events (warning/orange).
const STATUS_TONE: Record<TeamMemberStatus, "g-giving" | "g-events" | "g-people" | "g-home"> = {
  active: "g-giving",
  pending: "g-home",
  disabled: "g-people",
  locked: "g-people",
};

// CHG-2958 (Slice J / UM-R12) — Sessions tab body. Fetches the live
// sessions sub-resource via react-query; the entity hint's Sessions tab
// data_source (team-member.entity-hint.yaml) is realized here because
// v2gen.py tabs are override-block-owned (data_source remains spec
// documentation, not generator input — see DRIFT-1153 family note).
// The tab itself is RBAC-hidden via GATE_CATALOG.team_detail.sessions_tab
// (alora-giving.platform.member.read — what the BE enforces), so this
// body only renders for authorized holders.
const SESSION_CELL_STYLE = { padding: "6px 10px", fontSize: 13 } as const;

function SessionsTabBody({ accountId }: { accountId: string }) {
  const q = useQuery({
    queryKey: ["team-members", "sessions", accountId] as const,
    queryFn: () => getTeamMemberSessions(accountId),
    staleTime: 30_000,
  });
  const muted = { padding: 16, fontSize: 13, color: "var(--color-text-muted)" };
  if (q.isLoading) {
    return createElement("div", { className: "card", style: muted }, "Loading sessions…");
  }
  if (q.isError) {
    return createElement(
      "div",
      { className: "card", style: muted, "data-role": "error" },
      "Could not load sessions. Try again, or check your access.",
    );
  }
  const sessions: TeamMemberSessionEntry[] = q.data ?? [];
  if (sessions.length === 0) {
    return createElement(
      "div",
      { className: "card", style: muted },
      "No sessions recorded for this account.",
    );
  }
  const header = createElement(
    "tr",
    null,
    ...["Channel", "Issued", "Expires", "Status", "IP address", "Device"].map((h) =>
      createElement(
        "th",
        { key: h, style: { ...SESSION_CELL_STYLE, textAlign: "left", fontWeight: 600 } },
        h,
      ),
    ),
  );
  const fmtTs = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : "—";
  const rows = sessions.map((sess) =>
    createElement(
      "tr",
      { key: sess.id, "data-session-id": sess.id },
      createElement("td", { style: SESSION_CELL_STYLE }, sess.channel || "—"),
      createElement("td", { style: SESSION_CELL_STYLE }, fmtTs(sess.issued_at)),
      createElement("td", { style: SESSION_CELL_STYLE }, fmtTs(sess.expires_at)),
      createElement(
        "td",
        { style: SESSION_CELL_STYLE },
        sess.revoked_at ? "Revoked" : sess.is_active ? "Active" : "Expired",
      ),
      createElement("td", { style: SESSION_CELL_STYLE }, sess.ip_address ?? "—"),
      createElement(
        "td",
        { style: { ...SESSION_CELL_STYLE, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } },
        sess.user_agent ?? "—",
      ),
    ),
  );
  return createElement(
    "div",
    { className: "card", style: { padding: 8, overflowX: "auto" } },
    createElement(
      "table",
      { style: { width: "100%", borderCollapse: "collapse" }, "data-table": "account-sessions" },
      createElement("thead", null, header),
      createElement("tbody", null, ...rows),
    ),
  );
}
// override-block:helpers END

export const teamMemberDetailConfig: DetailPageConfig<TeamMember> = {
  idParam: "accountId",
  queryKey: (id: string) => ["team-members", "detail", id] as const,
  queryFn: (id: string) => teamMembersApi.getTeamMember(id),
  backTo: "/team",
  // override-block:title BEGIN
  title: (r) => r.full_name,
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (r) => r.email ?? null,
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    // override-block:statcard-last-login BEGIN
    {
      id: "last_login",
      label: "Last login",
      value: r.last_login_at,
      format: "text",
      tone: "g-mygiving",
      render: createElement(
        "span",
        { className: "tabular" },
        r.last_login_at ? _fmt.formatDate(r.last_login_at, "relative") : "Never",
      ),
    },
    // override-block:statcard-last-login END
    // override-block:statcard-mfa BEGIN
    {
      // MFA renders via a yes/no badge because the entity-hint
      // boolean-badge format isn't natively supported by StatCardDef (the
      // shipped StatCardFormat union covers currency/number/percent/text/
      // date/badge but boolean-as-yes/no must be projected at the slot).
      id: "mfa",
      label: "MFA",
      value: r.mfa_enabled ? "Yes" : "No",
      format: "badge",
      tone: "g-giving",
    },
    // override-block:statcard-mfa END
    {
      id: "failed_logins",
      label: "Failed logins",
      value: r.failed_login_count,
      format: "number",
      tone: "g-events",
    },
    // override-block:statcard-status BEGIN
    {
      id: "status",
      label: "Status",
      value: STATUS_LABEL[r.status] ?? r.status,
      format: "text",
      tone: STATUS_TONE[r.status] ?? "g-home",
    },
    // override-block:statcard-status END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    // header_actions per team-member.entity-hint.yaml#pages.detail_v2.header_actions.
    // All forward-promised today — the underlying screen refs / commands
    // (experience.form.account-edit / resetAccountPassword / resetAccountMfa)
    // are not wired in v2 yet.
    { id: "edit", label: "Edit", variant: "secondary", permissionGate: toGate(GATE_CATALOG.team_detail["edit"], "disable") },
    { id: "reset-password", label: "Reset password", variant: "ghost", permissionGate: toGate(GATE_CATALOG.team_detail["reset-password"], "disable") },
    { id: "reset-mfa", label: "Reset MFA", variant: "ghost", permissionGate: toGate(GATE_CATALOG.team_detail["reset-mfa"], "disable") },
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    // 4 tabs per team-member.entity-hint.yaml#pages.detail_v2.tabs. Overview
    // is the canonical primary-record view; the other three bridge to
    // forward-promised sub-resources via the team-members API client and
    // render empty-state copy until the canonical endpoints land.
    {
      id: "overview",
      label: "Overview",
      render: (a, fmt) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
          createElement("div", null, "Name: ", a.full_name),
          createElement("div", null, "Email: ", a.email),
          createElement("div", null, "Role: ", a.role ?? "—"),
          createElement("div", null, "Status: ", STATUS_LABEL[a.status] ?? a.status),
          createElement(
            "div",
            null,
            "MFA: ",
            a.mfa_enabled ? "Enabled" : "Not enabled",
          ),
          createElement(
            "div",
            null,
            "Last login: ",
            a.last_login_at
              ? fmt.formatDate(a.last_login_at, "medium")
              : "Never",
          ),
          createElement(
            "div",
            null,
            "Failed logins: ",
            String(a.failed_login_count),
          ),
          a.locked_until
            ? createElement(
                "div",
                null,
                "Locked until: ",
                fmt.formatDate(a.locked_until, "medium"),
              )
            : null,
          createElement(
            "div",
            null,
            "Created: ",
            fmt.formatDate(a.created_at, "medium"),
          ),
        ),
    },
    // Activity / Permissions tabs are forward-promised — the canonical
    // /users/{id}/{activity,permissions} endpoints haven't shipped; each
    // body renders explicit "coming soon" copy citing the dependency.
    // Sessions is WIRED as of CHG-2958 (Slice J) via the platform-members
    // sessions endpoint (the /users/{id}/sessions path never shipped).
    {
      id: "activity",
      label: "Activity",
      render: (_a) =>
        createElement(
          "div",
          {
            className: "card",
            style: {
              padding: 16,
              fontSize: 13,
              color: "var(--color-text-muted)",
            },
          },
          "No activity recorded yet. (Coming soon: GET /users/{id}/activity.)",
        ),
    },
    {
      id: "permissions",
      label: "Permissions",
      // CHG-1301 — Permissions tab visibility gated on user.role_update.
      // BE permission seeded in specs/seeds/user-management/permissions.yaml
      // + registered in src/api/app/core/deps.py _PERMISSION_ROLE_MAP
      // (org-admin + platform-admin only). DetailPageTab.permission
      // (CHG-1292) hides the tab in the tab strip when the user lacks the
      // code.
      permission: GATE_CATALOG.team_detail["permissions_tab"].permission,
      render: (_a) =>
        createElement(
          "div",
          {
            className: "card",
            style: {
              padding: 16,
              fontSize: 13,
              color: "var(--color-text-muted)",
            },
          },
          "Permission resolver lands when role + role-permission entities port (forward-promised).",
        ),
    },
    {
      id: "sessions",
      label: "Sessions",
      // CHG-2958 (Slice J / UM-R12) — wired to the live
      // GET /platform/members/accounts/{id}/sessions endpoint (CHG-2957).
      // Tab hidden for holders without platform.member.read (the exact
      // code the BE enforces on the route — NOT user.read).
      permission: GATE_CATALOG.team_detail["sessions_tab"].permission,
      render: (a) => createElement(SessionsTabBody, { accountId: a.id }),
    },
    // override-block:custom-tabs END
  ],
};
